import {
  BRAND,
  Button,
  buttonClassName,
  Callout,
  ErrorCode,
  helpUrlFor,
  type I18n,
} from '@signbento/ui'
import { Component, useEffect, useState, type ReactElement, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { reloadToLatest } from '../lib/app-update.js'
import { recordDiagnostic } from '../lib/diagnostics/store.js'
import {
  dismissNewDeviceHint,
  newDeviceHintVisible,
  routeFailureFor,
  type RouteFailure,
} from '../lib/shell-state.js'
import { useOnline } from '../lib/use-online.js'
import type { LocalVault } from '@signbento/storage'

/**
 * The shell's own states (PRD §9.3 "never blank the whole dashboard", PLT-10, Addendum E2 #8):
 * a page that failed, a path that doesn't exist, a browser that won't keep data, no network, and
 * the one-time hint on a device that starts empty.
 */

/** The support code a failure the app caught itself carries (the client's catch-all code). */
const UNEXPECTED_CODE = 'OP_UNEXPECTED'

const FAILURE_KEYS = {
  update: {
    title: 'shell.page_update',
    body: 'shell.page_update_body',
    recipient_body: 'shell.page_update_body_recipient',
  },
  offline: {
    title: 'shell.page_offline',
    body: 'shell.page_offline_body',
    recipient_body: 'shell.page_offline_body_recipient',
  },
  crash: {
    title: 'shell.page_failed',
    body: 'shell.page_failed_body',
    recipient_body: 'shell.page_failed_body_recipient',
  },
} as const

interface BoundaryProps {
  readonly i18n: I18n
  /** A change of page clears the failure, so the nav keeps working after one page broke. */
  readonly resetKey: string
  /**
   * A recipient, a CC reader or the framed challenge page: there is no Home of theirs to go to,
   * and no drafts or documents of theirs on this device to reassure them about.
   */
  readonly recipient?: boolean
  readonly children: ReactNode
}

interface BoundaryState {
  readonly failure: RouteFailure | null
}

/**
 * One page failing never blanks the app: a page whose code couldn't load (an old tab after a
 * deploy, a page not yet available offline) or that threw while rendering is replaced by a
 * sentence, Reload and, in the sender app, Go to Home. The navigation around it stays.
 */
export class RouteErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { failure: null }

  static getDerivedStateFromError(error: unknown): BoundaryState {
    return { failure: routeFailureFor(error, typeof navigator === 'undefined' || navigator.onLine) }
  }

  override componentDidCatch(error: unknown): void {
    recordDiagnostic({
      event_code:
        routeFailureFor(error, true) === 'crash' ? 'app.render_failed' : 'app.chunk_load_failed',
      severity: 'error',
      context: { reason: error instanceof Error ? error.name : 'unknown' },
    })
  }

  override componentDidUpdate(previous: BoundaryProps): void {
    if (previous.resetKey !== this.props.resetKey && this.state.failure !== null) {
      this.setState({ failure: null })
    }
    this.watchConnection()
  }

  override componentWillUnmount(): void {
    window.removeEventListener('online', this.retryOnline)
  }

  /**
   * A page that couldn't load offline loads itself again once the connection is back, instead of
   * leaving the offline sentence up after the offline banner has gone. A failed code load is
   * cached by the browser's module loader, so only a reload fetches it again.
   */
  private readonly retryOnline = (): void => {
    if (this.state.failure === 'offline') reloadToLatest()
  }

  private watchConnection(): void {
    if (typeof window === 'undefined') return
    window.removeEventListener('online', this.retryOnline)
    if (this.state.failure === 'offline') window.addEventListener('online', this.retryOnline)
  }

  override render(): ReactNode {
    const { failure } = this.state
    if (failure === null) return this.props.children
    return (
      <RouteFailureView
        i18n={this.props.i18n}
        failure={failure}
        recipient={this.props.recipient ?? false}
      />
    )
  }
}

function RouteFailureView({
  i18n,
  failure,
  recipient,
}: {
  readonly i18n: I18n
  readonly failure: RouteFailure
  readonly recipient: boolean
}): ReactElement {
  const keys = FAILURE_KEYS[failure]
  return (
    <div className="app-page app-shell-state" data-testid="route-failure" data-failure={failure}>
      <Callout tone={failure === 'crash' ? 'danger' : 'info'} role="alert">
        <p className="app-shell-state__title">{i18n.t(keys.title)}</p>
        <p>{i18n.t(recipient ? keys.recipient_body : keys.body)}</p>
        {failure === 'crash' ? (
          <ErrorCode text={i18n.t('error.code_line', { code: UNEXPECTED_CODE })} />
        ) : null}
      </Callout>
      <div className="app-shell-state__actions">
        <Button variant="primary" data-testid="route-failure-reload" onClick={reloadToLatest}>
          {i18n.t('common.reload')}
        </Button>
        {recipient ? null : (
          // A full navigation, not a client one: the shell itself may be what is out of date.
          <a className={buttonClassName({ variant: 'quiet' })} href="/">
            {i18n.t('shell.go_home')}
          </a>
        )}
      </div>
    </div>
  )
}

/** A path the app doesn't know: say so, and offer the two places most links mean. */
export function NotFound({ i18n }: { readonly i18n: I18n }): ReactElement {
  return (
    <div className="app-page app-shell-state" data-testid="not-found">
      <h1 className="app-shell-state__heading">{i18n.t('shell.not_found_title')}</h1>
      <p>{i18n.t('shell.not_found_body')}</p>
      <div className="app-shell-state__actions">
        <Link className={buttonClassName({ variant: 'primary' })} to="/">
          {i18n.t('shell.go_home')}
        </Link>
        <Link className={buttonClassName({ variant: 'quiet' })} to="/agreements">
          {i18n.t('shell.go_agreements')}
        </Link>
      </div>
    </div>
  )
}

/**
 * The browser won't let the app keep data here (a private window, blocked site data, a full
 * device, a broken key store). Nothing local can work, so this replaces the pages instead of
 * leaving them loading forever.
 */
export function StorageUnavailable({
  i18n,
  reason,
}: {
  readonly i18n: I18n
  readonly reason: string
}): ReactElement {
  const product = { product: BRAND.product_name }
  useEffect(() => {
    recordDiagnostic({
      event_code: 'vault.open_failed',
      severity: 'error',
      context: { reason: reason.slice(0, 64) },
    })
  }, [reason])
  return (
    <div className="app-page app-shell-state" data-testid="storage-unavailable">
      <h1 className="app-shell-state__heading">{i18n.t('shell.storage_title', product)}</h1>
      <p>{i18n.t('shell.storage_body', product)}</p>
      <ErrorCode text={i18n.t('error.code_line', { code: UNEXPECTED_CODE })} />
      <div className="app-shell-state__actions">
        <Button variant="primary" onClick={() => window.location.reload()}>
          {i18n.t('common.reload')}
        </Button>
        <a
          className={buttonClassName({ variant: 'quiet' })}
          href={helpUrlFor({ code: UNEXPECTED_CODE, recovery_action: null, retryable: true })}
        >
          {i18n.t('shell.contact_support')}
        </a>
      </div>
    </div>
  )
}

/** No network: local work goes on, and the person knows why a send will wait. */
export function OfflineNotice({ i18n }: { readonly i18n: I18n }): ReactElement | null {
  const online = useOnline()
  if (online) return null
  return (
    <p className="app-offline" role="status" data-testid="app-offline">
      {i18n.t('shell.offline')}
    </p>
  )
}

/**
 * A returning account signed in on a device that starts empty: its documents are on the device
 * where they were made, and a backup brings them over (PRD §2.7, Settings → Data).
 */
export function NewDeviceHint({
  i18n,
  vault,
}: {
  readonly i18n: I18n
  readonly vault: LocalVault | null
}): ReactElement | null {
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    if (vault === null) return
    let cancelled = false
    void newDeviceHintVisible(vault)
      .then((show) => {
        if (!cancelled) setVisible(show)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [vault])
  if (!visible || vault === null) return null
  return (
    <div className="app-new-device" data-testid="new-device-hint">
      <Callout tone="info" role="status" title={i18n.t('shell.new_device_title')}>
        <p>{i18n.t('shell.new_device_body')}</p>
        <div className="app-shell-state__actions">
          <Link
            className={buttonClassName({ variant: 'secondary', size: 'sm' })}
            // No `return`: its link reads "Back to your draft", and Home is one tap away anyway.
            to="/settings?section=data"
            data-testid="new-device-hint-action"
          >
            {i18n.t('shell.new_device_action')}
          </Link>
          <Button
            variant="quiet"
            size="sm"
            data-testid="new-device-hint-dismiss"
            // Hidden once the choice is saved (a few milliseconds), so a reload right away can't
            // bring it back.
            onClick={() => {
              void dismissNewDeviceHint(vault)
                .catch(() => undefined)
                .finally(() => setVisible(false))
            }}
          >
            {i18n.t('shell.dismiss')}
          </Button>
        </div>
      </Callout>
    </div>
  )
}
